/**
 * /terms — NDORFlow Terms of Service.
 *
 * RC 1.35.F1 P0-3.
 *
 * This page is the canonical Terms document linked from: signup
 * footer, plan-selection trust strip, candidate shell legal footer,
 * AccountPage > Privacy tab. It is a public route — no auth gate —
 * so Stripe's live-mode review team can load it directly.
 */
import LegalPageShell from './LegalPageShell';

export default function TermsPage() {
  return (
    <LegalPageShell
      title="Terms of Service"
      updatedAt="28 April 2026"
      activePath="/terms"
      testid="terms-page"
    >
      <p>
        These Terms of Service (the <strong>"Terms"</strong>) govern your access to and use
        of NDORFlow (<strong>"NDORFlow"</strong>, the <strong>"Service"</strong>). NDORFlow
        is a software-as-a-service product that helps candidates tailor CVs, generate cover
        letters, analyse job fit, and assemble application packs.
      </p>

      <h2>1. The parties to these Terms</h2>
      <p>
        NDORFlow is operated as a partnership between two companies. These Terms are entered
        into between you and:
      </p>
      <ul>
        <li>
          <strong>IAA Energy Resources Limited</strong> (Company No. 11583381), a company
          registered in England and Wales, whose registered office is 71-75 Shelton Street,
          Covent Garden, London, England, WC2H 9JQ (<strong>"we"</strong>,{' '}
          <strong>"us"</strong>, or <strong>"our"</strong>). IAA Energy Resources Limited
          is the Authorised Commercial Operator for NDORFlow and is responsible for
          marketing, sales, subscriptions, billing, payment collection, commercial
          onboarding and customer support. IAA Energy Resources Limited is the contracting
          party for your subscription and the counterparty on invoices and payment
          statements.
        </li>
      </ul>
      <p>
        The NDORFlow software platform and its associated intellectual property are owned,
        developed and licensed by <strong>NDOR Labs Limited</strong> (the "
        <strong>Software Owner</strong>"), which licenses the platform to IAA Energy
        Resources Limited for commercial distribution and customer access. Section&nbsp;10
        (<em>Software ownership &amp; licensing</em>) sets out the ownership and licensing
        arrangement in more detail. Customers purchase access to NDORFlow through IAA
        Energy Resources Limited.
      </p>
      <p>
        By creating an account, subscribing to a paid plan, or using any part of the
        Service, you agree to these Terms. If you do not agree, do not use the Service.
      </p>

      <h2>2. The Service</h2>
      <p>
        NDORFlow provides tools that assist you in preparing job applications. The Service includes a
        jobs catalogue, CV tailoring, cover letter generation, fit-scoring, interview preparation, and
        export utilities. The Service uses artificial-intelligence models and third-party processors
        (see our <a href="/privacy">Privacy Policy</a>) to produce its outputs.
      </p>

      <h2>3. Eligibility &amp; Account</h2>
      <ul>
        <li>You must be at least 16 years old and able to form a binding contract.</li>
        <li>You must provide accurate registration information and keep it current.</li>
        <li>You are responsible for all activity under your account and for keeping credentials secure.</li>
        <li>One account per person unless we explicitly permit team accounts.</li>
      </ul>

      <h2>4. Credits Model</h2>
      <p>
        Paid actions in NDORFlow (Analyse Fit, CV generation, cover-letter generation, and any other
        action we label as paid) consume <strong>Credits</strong>. Credits come from two sources:
      </p>
      <ul>
        <li><strong>Included Credits</strong> — allocated monthly by your subscription plan; they reset each billing month and do not carry over.</li>
        <li><strong>Top-up Credits</strong> — purchased as a top-up package. Top-up Credits do not expire and remain on your account until consumed, independent of your subscription state. Top-up Credits are consumed only after your Included Credits for the current month are exhausted.</li>
      </ul>
      <p>
        Credits are consumed strictly for the paid action they are charged for; pricing for each action
        is displayed before you commit. Where an action produces no improvement over your existing
        materials, NDORFlow may waive the charge — the decision and reason are surfaced in the success
        toast at the point of action.
      </p>
      <p>
        Credits are a licence to use the Service. They have no cash value, are not redeemable for
        money, and are non-transferable between accounts.
      </p>

      <h2>5. Subscriptions &amp; Billing</h2>
      <ul>
        <li>Paid plans (Focus, Flow) are billed monthly or annually in advance via Stripe.</li>
        <li>
          IAA Energy Resources Limited is the seller and merchant of record for your
          subscription and any top-up purchase. Card processing is performed by Stripe on
          behalf of IAA Energy Resources Limited; invoices, receipts and payment statements
          will show "IAA Energy Resources Limited" as the counterparty.
        </li>
        <li>Your subscription renews automatically at the end of each billing period unless cancelled.</li>
        <li>Prices, Included-Credit allowances, and any Founding Member offers are listed on the plan-selection page at the time of purchase and in your Account &gt; Plan.</li>
        <li>We may change pricing or plan features with at least 30 days' notice for existing subscribers; changes take effect at the next renewal.</li>
        <li>Failed payments may result in Service suspension; you remain liable for amounts owed.</li>
      </ul>

      <h2>6. Cancellation &amp; Refunds</h2>
      <p>
        You may cancel your subscription at any time from Account &gt; Plan or via the Stripe Customer
        Portal. Cancellation takes effect at the end of your current billing period and you retain
        access until that date. Refund eligibility is governed by our <a href="/refund">Refund Policy</a>,
        which is incorporated into these Terms by reference. Refunds are issued by IAA Energy Resources
        Limited to the original payment method.
      </p>

      <h2>7. AI-Generated Content &amp; Disclaimers</h2>
      <p>
        CVs, cover letters, fit scores, interview preparation, and other outputs are generated with the
        assistance of artificial-intelligence models (OpenAI, Anthropic, and Google
        accessed via NDORFlow's managed integrations). These outputs are probabilistic and may contain
        errors, omissions, or fabricated content. You must review, edit, and verify every output
        before using it in a real job application. NDORFlow is not liable for any consequence arising
        from unreviewed output.
      </p>
      <p>
        <strong>No employment guarantee.</strong> NDORFlow is a tool that helps you prepare
        applications. We do not promise interviews, job offers, salary outcomes, or any specific
        employment result. Success depends on many factors outside our control.
      </p>

      <h2>8. Your Content</h2>
      <p>
        You retain all ownership of the CVs, cover letters, application packs, and other materials you
        upload or generate using the Service (your <strong>"Content"</strong>). You grant NDORFlow a
        limited, non-exclusive, worldwide licence to host, process, and display your Content solely to
        provide the Service to you. We do not use your Content to train public AI models and we do not
        sell your Content.
      </p>

      <h2>9. Acceptable Use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Service to generate or submit falsified credentials or fraudulent applications.</li>
        <li>Reverse-engineer, scrape at scale, or copy substantial parts of the Service.</li>
        <li>Use the Service to harass, defame, or discriminate against any person.</li>
        <li>Upload malware, illegal content, or content you do not have rights to.</li>
        <li>Circumvent our Credits, rate-limit, or billing controls.</li>
        <li>Use the Service to build a competing product.</li>
      </ul>

      <h2>10. Software ownership &amp; licensing</h2>
      <p>
        The NDORFlow software platform, its underlying source code, models, prompts,
        evaluation frameworks, algorithms, databases, templates, documentation, branding
        and all other associated intellectual property (together, the{' '}
        <strong>"Platform IP"</strong>) are owned exclusively by{' '}
        <strong>NDOR Labs Limited</strong>. Nothing in these Terms, in your subscription, or
        in any commercial dealing with IAA Energy Resources Limited transfers any ownership
        of the Platform IP to you.
      </p>
      <p>
        NDOR Labs Limited licenses the Platform IP to IAA Energy Resources Limited on
        commercial terms that authorise IAA Energy Resources Limited to distribute the
        Service, sell subscriptions, invoice customers and provide customer support. Your
        subscription grants you a limited, personal, non-exclusive, non-transferable,
        revocable licence to use the Service subject to these Terms; it does not grant you
        any right in the Platform IP itself. Our third-party processors retain all rights
        to their underlying models and platforms.
      </p>

      <h2>11. Suspension &amp; Termination</h2>
      <p>
        We may suspend or terminate your account, with or without notice, if you materially breach
        these Terms, if required by law, or if continued provision creates material risk to NDORFlow
        or other users. You may close your account at any time from Account &gt; Plan. On termination,
        your right to use the Service ends and we may delete your data subject to the retention rules
        in our <a href="/privacy">Privacy Policy</a>.
      </p>

      <h2>12. Limitation of Liability</h2>
      <p>
        To the maximum extent permitted by law, NDORFlow, IAA Energy Resources Limited, NDOR
        Labs Limited and their respective licensors are not liable for any indirect,
        incidental, special, consequential, or punitive damages, including loss of
        employment opportunity, loss of income, loss of data, or reputational harm. The
        aggregate liability of IAA Energy Resources Limited for any claim arising from or
        related to the Service shall not exceed the greater of (a) the amount you paid IAA
        Energy Resources Limited in the twelve (12) months preceding the event giving rise
        to the claim, or (b) one hundred pounds sterling (£100). Nothing in these Terms
        limits liability for fraud, gross negligence, or any liability that cannot be
        limited by law (including liability for death or personal injury caused by
        negligence).
      </p>

      <h2>13. Warranty Disclaimer</h2>
      <p>
        The Service is provided "as is" and "as available" without warranties of any kind, express or
        implied, including fitness for a particular purpose, non-infringement, and accuracy. We do not
        warrant uninterrupted or error-free operation.
      </p>

      <h2>14. Changes to these Terms</h2>
      <p>
        We may update these Terms from time to time. Material changes will be notified by email and/or
        in-product banner at least 14 days before they take effect. Continued use of the Service after
        changes take effect constitutes acceptance of the updated Terms.
      </p>

      <h2>15. Governing Law &amp; Disputes</h2>
      <p>
        These Terms are governed by the laws of England and Wales. Any dispute arising under these
        Terms is subject to the exclusive jurisdiction of the courts of England and Wales, except where
        mandatory consumer-protection law grants you a right to bring proceedings in your country of
        residence.
      </p>

      <h2>16. Contact</h2>
      <p>
        For questions about these Terms, email <a href="mailto:support@ndor.app">support@ndor.app</a>.
        For billing issues, email <a href="mailto:support@ndor.app">support@ndor.app</a>.
      </p>
    </LegalPageShell>
  );
}
